/* 루미나 화면 층 신작 — 홈 전용 부품(히어로 열차·신규게임 원호 캐러셀·추천게임 세로 흐름·게임사 그리드)이 함께 쓰는 아이콘·게임 카드 규칙.
   WHY: SPEC §2 — 블랙 HomePage.jsx 슬라이드 카드의 데이터 규칙(썸네일 우선순위·404 폴백·이름·게임사 표기)을 한 곳에 두어
        여러 홈 구역이 같은 규칙을 쓰게 한다. 값은 전부 유저 API 응답에서 온다(예시값 없음). */
import React from 'react';
import { resolveApiImageUrl } from '../../utils/logoUtils';

/* 블랙 BlackGameSlideRow 의 썸네일 404 폴백 — 깨진 이미지 아이콘 대신 투명 픽셀로 바꿔 카드 바탕(네이비)이 보이게 한다(재발 루프 방지 가드 포함) */
export const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const onThumbError = (e) => {
  if (!e.currentTarget.dataset.errorHandled) {
    e.currentTarget.dataset.errorHandled = 'true';
    e.currentTarget.src = TRANSPARENT_PIXEL;
  }
};

/* 블랙 슬라이드 카드와 같은 우선순위: thumbnail_url → image_url → 기본 썸네일 */
export const gameThumbSrc = (game) => game.thumbnail_url || game.image_url || '/img/default-game-thumbnail.jpg';
export const gameDisplayName = (game) => game.game_name_ko || game.game_name;
/* 블랙 슬라이드 카드의 공급사 뱃지와 같은 우선순위 */
export const gameVendorLabel = (game) => game.vendor_name_ko || game.vendor || game.provider || '';
/* 게임 종류 필 — 응답의 game_type 이 슬롯/카지노일 때만 표기(알 수 없는 값은 표기하지 않는다) */
const GAME_TYPE_LABEL = { slot: '슬롯', casino: '카지노' };
export const gameTypeLabel = (game) => GAME_TYPE_LABEL[String(game.game_type || '').toLowerCase()] || '';

/* 게임사 식별 코드 — 블랙 ProviderGrid 와 같은 우선순위(게임사 응답마다 이름 칸이 달라 차례로 본다) */
export const providerCodeOf = (provider) => provider.provider ||
  provider.code ||
  provider.provider_name ||
  provider.name ||
  provider.id ||
  provider.vendor ||
  provider.category_name ||
  'Unknown Provider';

/* 게임사 캐릭터 그림 경로 { png, webp } — 없으면 null.
   규칙(블랙 ProviderGrid 와 같음): 관리자가 '제거'(character_hidden)하면 그리지 않는다 → 응답의 character_url 우선 →
   없으면 기본 경로(/api/providers/characters/<종류>-<코드>.png). 업로드 캐릭터(/uploads/*)는 같은 이름의 webp 가 없어 webp 를 주지 않는다.
   WHY: 홈 게임사 카드와 히어로 게임 카드가 같은 게임사 그림을 써야 해서 규칙을 한 곳에 둔다(2026-09-28). */
export const providerCharacterSources = (provider, type) => {
  if (!provider || provider.character_hidden === true) return null;
  const baseProviderName = String(providerCodeOf(provider)).replace(/_casino|_slot/gi, '').toLowerCase();
  const png = provider.character_url
    ? resolveApiImageUrl(provider.character_url)
    : `/api/providers/characters/${type}-${baseProviderName}.png`;
  const webp = (provider.character_url && /\/uploads\//.test(provider.character_url))
    ? null
    : png.replace(/\.png$/i, '.webp');
  return { png, webp };
};

/* 움직임 최소화 설정(prefers-reduced-motion) — 시안 규칙: 자동 이동·인트로만 끄고 배치는 그대로 */
export const prefersReducedMotion = () => (
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/* 시안 화살표(1차 버튼 원형·카드 원형 버튼 공용) */
export const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/* 시안 .btn-icon — 52px 그라디언트 링 + 민트 원, 호버 시 화살표가 옆으로 밀려 들어온다(.group:hover) */
export const IconBtn = () => (
  <span className="btn-icon" aria-hidden="true">
    <span className="in">
      <span className="ar"><i><ArrowIcon /></i><i><ArrowIcon /></i></span>
    </span>
  </span>
);

/* 키보드 접근: 카드(role=button)에서 Enter/Space 로 클릭과 같은 동작 */
export const onEnterOrSpace = (fn) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fn(e);
  }
};

/* ── 첫 화면 카드 조립(홈 첫 화면 카드 줄) ──
   [2026-09-30] 홈 HomePage.jsx 안에 있던 규칙을 그대로 옮겼다 — 로그인 패널도 같은 카드(관리자 '첫 화면 카드')를 부채꼴로 그리려고 한 곳에 뒀다.
   [2026-10-01] 로그인 패널은 운영자 지시("이걸 그냥 없애줘")로 없앴고, 지금은 홈만 쓴다(규칙은 여기 한 곳 그대로).
   카드 항목(관리자 HeroCardsEditor 저장, design_tokens.hero_cards): { id, type:'event'|'game'|'provider', enabled, image?, overlay?, title?,
     game?:{id,name,vendor,thumbnail}, provider?:{code,gameType,name} } — 게임·게임사 카드의 image·overlay·title 은 '바꾸기'(비우면 자동).
   실행할 수 없는 대상(게임사가 노출·선택·게임종류 제한으로 목록에서 빠짐)은 빼고, 그림·캐릭터·제목이 모두 없는 이벤트 카드도 뺀다. */
export const isVideoSrc = (src) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(String(src || ''));

/* 디자인 토큰의 첫 화면 카드 목록 — 다른 토큰과 같은 파싱 규칙(문자열이면 JSON 으로 풀고, 실패하면 빈 목록) */
export const parseHeroCardTokens = (designTemplate) => {
  let dt = designTemplate?.design_tokens;
  if (typeof dt === 'string') {
    try { dt = JSON.parse(dt); } catch (e) { return []; }
  }
  return Array.isArray(dt?.hero_cards) ? dt.hero_cards : [];
};

/* 카드 목록 → 화면용 카드 [{ key, kind, bg:{src,video}|null, chr:{src,fallback}|null, title, game?, provider?, gameType? }]
   slotProviders·casinoProviders = 유저 API 게임사 목록(노출·선택·게임종류 게이트가 이미 적용된 목록), providerName = 게임사 표시 이름 규칙 */
export const buildHeroCards = (tokens, { slotProviders = [], casinoProviders = [], providerName }) => {
  const findProvider = (list, code) => {
    const want = String(code || '').toUpperCase();
    return want ? list.find((p) => String(providerCodeOf(p)).toUpperCase() === want) : null;
  };
  /* 자동 캐릭터(게임사 캐릭터) → 화면용 { src, fallback }: webp 를 먼저, 없으면 png */
  const autoChr = (provider, type) => {
    const c = providerCharacterSources(provider, type);
    return c ? { src: c.webp || c.png, fallback: c.webp ? c.png : null } : null;
  };
  const pickText = (v) => (typeof v === 'string' ? v.trim() : '');
  return (Array.isArray(tokens) ? tokens : [])
    .filter((c) => c && c.enabled !== false)
    .map((c, i) => {
      const key = c.id || `hc-${i}`;
      const ownBg = pickText(c.image) ? { src: c.image.trim(), video: isVideoSrc(c.image) } : null;
      const ownChr = pickText(c.overlay) ? { src: c.overlay.trim(), fallback: null } : null;
      const ownTitle = pickText(c.title);
      if (c.type === 'game') {
        const g = c.game;
        if (!g || !g.id) return null;
        const provider = findProvider(slotProviders, g.vendor);
        if (!provider) return null; // 게임사가 목록에서 빠짐 = 실행할 수 없음
        return {
          key, kind: 'game',
          bg: ownBg || { src: gameThumbSrc({ thumbnail_url: g.thumbnail }), video: false },
          chr: ownChr || autoChr(provider, 'slot'),
          title: ownTitle || g.name || '',
          game: { id: g.id, game_name_ko: g.name, game_name: g.name, thumbnail_url: g.thumbnail, vendor: g.vendor },
        };
      }
      if (c.type === 'provider') {
        const pr = c.provider;
        if (!pr || !pr.code) return null;
        const type = pr.gameType === 'casino' ? 'casino' : 'slot';
        const provider = findProvider(type === 'casino' ? casinoProviders : slotProviders, pr.code);
        if (!provider) return null;
        return {
          key, kind: 'provider', gameType: type, provider,
          bg: ownBg,
          chr: ownChr || autoChr(provider, type),
          title: ownTitle || (providerName ? providerName(provider) : (provider.name || '')),
        };
      }
      /* 이벤트 카드 — 그림·캐릭터·제목 중 하나도 없으면 빈 카드라 뺀다 */
      if (!ownBg && !ownChr && !ownTitle) return null;
      return { key, kind: 'event', bg: ownBg, chr: ownChr, title: ownTitle };
    })
    .filter(Boolean);
};
